import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import './Css/PlanCheckTracker.css';
import './Css/PlanCheckTracker-responsive.css';
import { Ripple } from "primereact/ripple";
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import NoDataCreated from "../../../../../Components/NoDataCreated";
import ErrorPage from "../../../../../Components/ErrorPage";
import PageLoader from "../../../../../Components/PageLoader";
import { goToTop } from "../../../../../Components/goToTop";
import { getLoginUserRoleName, UserRoleName } from "../../../../../Utils/LoginUserRoleName";
import { PlanCheckTrackerTemplateList } from "../../../../../Controllers/Types/PlanCheck/PlanCheckTrackerType";
import { useAppDispatch, useAppSelector } from "../../../../../Controllers/hooks";
import { getAllPlanChckTrackerList } from "../../../../../Controllers/Thunks/PlanCheckTrackerThunks";
import { createPlanCheckTracker } from "../../../../../Api/PlanCheck/PlanCheckApiServices";
import { PlanCheckTracker as PlanCheckTrackerType } from "../../../../../Controllers/Types/PlanCheck/PlanCheckTrackerType";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";

const PlanCheckTracker: React.FC = () => {
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const location = useLocation();
    const projectData = location.state?.data;
    const serviceId = Number(projectData.folder_id);

    const loadingPage = useAppSelector((state) => state?.planCheckTrackerList?.loader);
    const error = useAppSelector((state) => state?.planCheckTrackerList?.errorDisplay);

    const [loginUserRoleName, setLoginUserRoleName] = useState<UserRoleName | ''>('');

    const [showPlanCheckTrackerOptionsModal, setShowPlanCheckTrackerOptionsModal] = useState<boolean>(false);
    const [basicLoading, setBasicLoading] = useState<boolean>(false);
    const [detailLoading, setDetailLoading] = useState<boolean>(false);

    const [showCustomOptionModal, setShowCustomOptionModal] = useState<boolean>(false);

    const [showTemplatesModal, setShowTemplatesModal] = useState<boolean>(false);
    const [planCheckTrackerTemplatesData, setPlanCheckTrackerTemplatesData] = useState<PlanCheckTrackerTemplateList[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<PlanCheckTrackerTemplateList | null>(null);

    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) => hasPermission(state, screen, permission));
    };

    const hasPermissionAddPlanCheckTracker = useCanEdit('Plan Check Tracker', 'add_plan_check_tracker');

    const dispatch = useAppDispatch();

    useEffect(() => {
        const loadData = async () => {
            const data = await dispatch(getAllPlanChckTrackerList(serviceId));
            const payload = data.payload as PlanCheckTrackerType[];

            if (payload && payload.length > 0) {
                navigate('list', { state: { data: projectData } });
            }
        };

        loadData();
    }, [dispatch, navigate, projectData, serviceId]);

    useEffect(() => {
        const fetchUserRole = async () => {
            const roleName = await getLoginUserRoleName();
            setLoginUserRoleName(roleName);
        };

        fetchUserRole();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setPlanCheckTrackerTemplatesData([])
            // setPlanCheckTrackerTemplatesData(planCheckTemplateLists)
        }
        fetchData();
    }, []);


    /* ****************************** Plan check tracker ****************************** */
    /* Plan check tracker */
    const handleCreatePlanCheckTracker = () => {
        setShowPlanCheckTrackerOptionsModal(true);
    }

    const handleClosePlanCheckTrackerModal = () => {
        setShowPlanCheckTrackerOptionsModal(false);
    }

    const planCheckTrackerOptionModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Select plan check tracker option
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleClosePlanCheckTrackerModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );
    /*  */


    /* For template */
    const handleShowTemplatesModal = () => {
        setShowTemplatesModal(true);
    }

    const handleCloseTemplatesModal = () => {
        setShowTemplatesModal(false);
    }

    const templatesModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Plan check tracker templates
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseTemplatesModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const handleSelectPlanCheckTrackerTemplate = (template: PlanCheckTrackerTemplateList) => {
        setSelectedTemplate(template);
    }
    /*  */


    /* For custom */
    const handleShowCustomOption = () => {
        setShowCustomOptionModal(true);
    }

    const handleCloseCustomOptionModal = () => {
        setShowCustomOptionModal(false);
    }

    const customOptionModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Select custom option type
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseCustomOptionModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const handleCreateCustomPlanCheckTracker = (mode: 'Basic' | 'Detail') => {
        confirmDialog({
            message: `Are you sure you want to create plan check tracker with ${mode} mode?`,
            header: 'Confirmation',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            accept: () => createCustomPlanCheckTracker(mode),
            dismissableMask: true,
            resizable: false,
            draggable: false,
        });
    }

    // database insert functions for create plan check tracker by archetect
    const createCustomPlanCheckTracker = async (mode: 'Basic' | 'Detail') => {
        if (mode === 'Basic') {
            setBasicLoading(true);
        } else {
            setDetailLoading(true);
        }

        const data = await createPlanCheckTracker(serviceId, mode);

        if (data.statusCode === 200) {
            // call dispach event
            dispatch(getAllPlanChckTrackerList(serviceId));

            if (mode === 'Basic') {
                setBasicLoading(false);
            } else {
                setDetailLoading(false);
            }
            handleCloseCustomOptionModal();

            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Plan check tracker created successfully.',
                    life: 2000,
                });
            }
            navigate(`list`, { state: { data: projectData } });
            goToTop();
        } else {
            if (mode === 'Basic') {
                setBasicLoading(false);
            } else {
                setDetailLoading(false);
            }

            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error creating the plan check tracker.',
                    life: 3000
                });
            }
        }
    }
    /*  */
    /* *************************************************************************** */


    /* **************************** Common functions ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingPage ? (
                    <PageLoader />
                ) : error ? (
                    <ErrorPage errorMessage={error} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData.job_number} : {projectData.address}
                                </li>
                                <li className="page_breadcrumb_item has_child">{projectData.folder}</li>
                                <li className="page_breadcrumb_item active">{projectData.feature_name}</li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_main_content_section p-0">
                            {loginUserRoleName === 'architect' ? (
                                <div className="no_data_created_area with_pointing">
                                    <img src="/assets/images/no_data/no_data_img.png" alt="No Data Found!" />
                                    <h6>
                                        No plan check tracker has been created.
                                        {hasPermissionAddPlanCheckTracker ? 'To create, click the Create button.' : ''}
                                    </h6>

                                    {hasPermissionAddPlanCheckTracker && (
                                        <button className="add_data_link_btn fill radius p-ripple" onClick={handleCreatePlanCheckTracker}>
                                            <i className="bi bi-plus-circle"></i>
                                            <span>Create plan check tracker</span>
                                            <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="no_data_created_area with_pointing">
                                    <img src="/assets/images/no_data/no_data_img.png" alt="No Data Found!" />
                                    <h6>
                                        No plan check tracker was issued by the architect!
                                    </h6>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>


            {/* Plan check tracker option modal */}
            <Dialog
                visible={showPlanCheckTrackerOptionsModal}
                header={planCheckTrackerOptionModalHeader}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_xsm"
                onHide={handleClosePlanCheckTrackerModal}
                dismissableMask
                draggable={false}
            >
                <div className="custom_modal_body">
                    <div className="custom_option_btn_area">
                        <button className="custom_pc_option_btn p-ripple"
                            onClick={handleShowTemplatesModal}>
                            <span>
                                <i className="bi bi-ui-checks-grid me-2"></i>
                                Template
                            </span>
                            <i className="bi bi-chevron-right"></i>
                            <Ripple />
                        </button>

                        <button className="custom_pc_option_btn p-ripple"
                            onClick={handleShowCustomOption}>
                            <span>
                                <i className="bi bi bi-pencil-square me-2"></i>
                                Custom
                            </span>
                            <i className="bi bi-chevron-right"></i>
                            <Ripple />
                        </button>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Custom option modal */}
            <Dialog
                visible={showCustomOptionModal}
                header={customOptionModalHeader}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_md"
                onHide={handleCloseCustomOptionModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="custom_option_area">
                        <div className="row">
                            <div className="col-12 col-sm-6 divide">
                                <div className="custom_option_container">
                                    <h6 className="custom_option_head">
                                        <i className="bi bi-card-checklist me-2"></i>
                                        Basic
                                    </h6>

                                    <p className="custom_option_desc">
                                        Lorem ipsum dolor sit amet consectetur adipisicing elit. Deserunt atque hic
                                    </p>

                                    <div className="custom_option_btn_container">
                                        <Button
                                            label={`${basicLoading ? 'Processing' : 'Create'}`}
                                            icon="bi bi-chevron-right"
                                            iconPos="right"
                                            onClick={() => handleCreateCustomPlanCheckTracker('Basic')}
                                            loading={basicLoading}
                                            className="custom_btn sm primary outlined"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-sm-6">
                                <div className="custom_option_container">
                                    <h6 className="custom_option_head">
                                        <i className="bi bi-card-list me-2"></i>
                                        Detail
                                    </h6>

                                    <p className="custom_option_desc">
                                        Lorem ipsum dolor sit amet consectetur adipisicing elit. Deserunt atque hic excepturi laboriosam qui natus iure modi aperiam in similique?
                                    </p>

                                    <div className="custom_option_btn_container">
                                        <Button
                                            label={`${detailLoading ? 'Processing' : 'Create'}`}
                                            icon="bi bi-chevron-right"
                                            iconPos="right"
                                            onClick={() => handleCreateCustomPlanCheckTracker('Detail')}
                                            loading={detailLoading}
                                            className="custom_btn sm primary outlined"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Template options modal */}
            <Dialog
                visible={showTemplatesModal}
                header={templatesModalHeader}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_lg"
                onHide={handleCloseTemplatesModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="plan_check_templates_area">
                        {planCheckTrackerTemplatesData?.length > 0 ? (
                            <React.Fragment>
                                <h6 className="plan_check_template_head">Choose a template:</h6>
                                <div className="row sow_template_row">
                                    {planCheckTrackerTemplatesData?.map((template) => (
                                        <div key={template.id} className="col-12 col-sm-6 col-lg-6 col-xl-4">
                                            <article className="sow_template p-ripple" style={{ backgroundColor: template.color_code }}
                                                onClick={() => handleSelectPlanCheckTrackerTemplate(template)}>
                                                <div className="temp_icon">
                                                    <img src={template.logo} alt={template.name} />
                                                </div>
                                                <h6>{template.name}</h6>
                                                <Ripple />
                                            </article>
                                        </div>
                                    ))}
                                </div>
                            </React.Fragment>
                        ) : (
                            <NoDataCreated
                                containerClassname="pos_rel"
                                message={`There are no templates for plan check tracker. Try creating one using the custom option.`}
                            />
                        )}
                    </div>
                </div>
            </Dialog>
            {/*  */}
        </React.Fragment>
    )
}

export default PlanCheckTracker;